import { useState, useEffect } from 'react';
import { PlacedLabel, LabelTemplate, PlacedBlock, SocketConnection, TextAnnotation } from '@/app/types/cad';
import { X } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Checkbox } from '@/app/components/ui/checkbox';

interface LabelEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: PlacedLabel | null;
  template: LabelTemplate | null;
  blocks?: PlacedBlock[];
  connections?: SocketConnection[];
  textAnnotations?: TextAnnotation[];
  onSave: (updates: Partial<PlacedLabel>) => void;
}

export function LabelEditDialog({
  open,
  onOpenChange,
  label,
  template,
  blocks,
  connections,
  textAnnotations,
  onSave
}: LabelEditDialogProps) {
  const [positionU, setPositionU] = useState(0);
  const [positionV, setPositionV] = useState(0);
  const [leaderLineVisible, setLeaderLineVisible] = useState(true);
  const [leaderLineColor, setLeaderLineColor] = useState('#333333');
  const [leaderLineWidth, setLeaderLineWidth] = useState(1);

  useEffect(() => {
    if (label) {
      setPositionU(label.position.u);
      setPositionV(label.position.v);
      setLeaderLineVisible(label.leaderLineVisible);
      setLeaderLineColor(label.leaderLineColor);
      setLeaderLineWidth(label.leaderLineWidth);
    }
  }, [label, open]);

  const handleSave = () => {
    onSave({
      position: { u: positionU, v: positionV },
      leaderLineVisible,
      leaderLineColor,
      leaderLineWidth
    });
    onOpenChange(false);
  };

  if (!open || !label || !template) return null;

  // Find connected element
  let connectedElementName = 'Nicht gefunden';
  if (label.targetElementType === 'block') {
    const block = blocks?.find(b => b.id === label.targetElementId);
    if (block) {
      connectedElementName = block.blockData.name;
    }
  } else if (label.targetElementType === 'connection') {
    const connection = connections?.find(c => c.id === label.targetElementId);
    if (connection) {
      connectedElementName = `Verbindung ${connection.id.slice(0, 8)}`;
    }
  } else if (label.targetElementType === 'text-annotation') {
    const text = textAnnotations?.find(t => t.id === label.targetElementId);
    if (text) {
      connectedElementName = `Text: ${text.text.slice(0, 30)}${text.text.length > 30 ? '...' : ''}`;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-[500px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Beschriftung bearbeiten</h2>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Template Info */}
          <div className="p-3 bg-gray-50 rounded">
            <div className="text-sm font-medium text-gray-700">Template</div>
            <div className="text-sm text-gray-600">{template.name}</div>
          </div>

          {/* Position */}
          <div className="space-y-2">
            <Label>Position</Label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs text-gray-500">U (Grid-Einheiten)</Label>
                <Input
                  type="number"
                  value={positionU}
                  onChange={(e) => setPositionU(parseFloat(e.target.value))}
                  step="0.5"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">V (Grid-Einheiten)</Label>
                <Input
                  type="number"
                  value={positionV}
                  onChange={(e) => setPositionV(parseFloat(e.target.value))}
                  step="0.5"
                />
              </div>
            </div>
          </div>

          {/* Leader Line Settings */}
          <div className="space-y-3">
            <Label>Verbindungslinie (Leader Line)</Label>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="leaderLineVisible"
                checked={leaderLineVisible}
                onCheckedChange={(checked) => setLeaderLineVisible(checked as boolean)}
              />
              <label
                htmlFor="leaderLineVisible"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Verbindungslinie anzeigen
              </label>
            </div>

            {leaderLineVisible && (
              <>
                <div>
                  <Label className="text-xs text-gray-500">Farbe</Label>
                  <div className="flex items-center space-x-2">
                    <Input
                      type="color"
                      value={leaderLineColor}
                      onChange={(e) => setLeaderLineColor(e.target.value)}
                      className="w-20 h-10"
                    />
                    <Input
                      type="text"
                      value={leaderLineColor}
                      onChange={(e) => setLeaderLineColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-gray-500">Linienstärke</Label>
                  <Input
                    type="number"
                    value={leaderLineWidth}
                    onChange={(e) => setLeaderLineWidth(parseFloat(e.target.value))}
                    min="0.5"
                    max="10"
                    step="0.5"
                  />
                </div>
              </>
            )}
          </div>

          {/* Resolved Data Preview */}
          <div className="space-y-2">
            <Label>Vorschau der aufgelösten Daten</Label>
            <div className="border rounded overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {label.resolvedData.map((row, rowIndex) => (
                    <tr key={rowIndex} className="border-b last:border-b-0">
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="p-2 border-r last:border-r-0 bg-gray-50"
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Connected Element */}
          <div className="space-y-2">
            <Label>Verbundenes Element</Label>
            <div className="p-2 bg-gray-50 rounded">
              {connectedElementName}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleSave}>
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}